<template>
  <div class="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
    <!-- Título y Badges -->
    <div>
      <div class="flex items-center gap-3">
        <h1 class="text-3xl font-bold text-gray-800 tracking-tight">{{ title }}</h1>
        <span 
          v-if="totalCount !== null && totalCount !== undefined" 
          class="px-2.5 py-0.5 text-xs font-semibold rounded-full bg-orange-100 text-orange-700"
        >
          {{ totalCount }}
        </span>
      </div>
      <p class="text-xs sm:text-sm text-gray-500 mt-1">
        Gestión, reportes e historial de movimientos de inventario en almacenes.
      </p>
    </div>

    <!-- Barra de Acciones del Header -->
    <div class="flex flex-wrap items-center gap-2.5 sm:gap-3 w-full md:w-auto justify-end">
      <!-- Botón Exportar (Dropdown con Reportes) -->
      <div ref="exportDropdownRef" class="relative">
        <button 
          type="button"
          class="px-3.5 py-2 bg-white border border-gray-300 hover:border-gray-400 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center gap-2 transition-colors font-medium shadow-sm text-sm cursor-pointer"
          @click.stop="toggleExportDropdown"
        >
          <i class="fa fa-download text-gray-500" />
          <span>{{ reportLabel }}</span>
          <i 
            class="fa fa-chevron-down text-[10px] text-gray-400 transition-transform duration-200" 
            :class="{'rotate-180': isExportOpen}"
          />
        </button>

        <!-- Dropdown Menu -->
        <div 
          v-if="isExportOpen" 
          class="absolute top-full right-0 mt-2 w-60 bg-white rounded-xl shadow-xl border border-gray-100 z-50 py-1.5 animate-in fade-in zoom-in-95 duration-150 overflow-hidden"
        >
          <a 
            :href="exportUrl" 
            target="_blank"
            class="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-emerald-50 hover:text-emerald-700 transition-colors"
            @click="isExportOpen = false"
          >
            <div class="w-7 h-7 rounded-lg bg-emerald-50 flex items-center justify-center shrink-0">
              <i class="fa fa-file-excel text-emerald-600 text-sm" />
            </div>
            <div class="text-left">
              <div class="font-bold text-xs text-gray-900">Exportar a Excel</div>
              <div class="text-[10px] text-gray-400">Descargar registros filtrados</div>
            </div>
          </a>

          <div class="border-t border-gray-100 my-1" />

          <button 
            type="button"
            class="w-full text-left px-4 py-2 text-xs text-gray-700 hover:bg-orange-50 hover:text-orange-600 flex items-center gap-2 transition-colors cursor-pointer" 
            @click="selectReport(null)"
          >
            <i class="fa fa-list text-gray-400 text-xs" />
            <span>Todos los registros</span>
          </button>

          <button 
            type="button"
            class="w-full text-left px-4 py-2 text-xs text-gray-700 hover:bg-orange-50 hover:text-orange-600 flex items-center gap-2 transition-colors cursor-pointer" 
            @click="selectReport('daily')"
          >
            <i class="fa fa-calendar-day text-orange-500 text-xs" />
            <span>Reporte Diario</span>
          </button>

          <button 
            type="button"
            class="w-full text-left px-4 py-2 text-xs text-gray-700 hover:bg-orange-50 hover:text-orange-600 flex items-center gap-2 transition-colors cursor-pointer" 
            @click="selectReport('custom')"
          >
            <i class="fa fa-calendar-alt text-orange-500 text-xs" />
            <span>Por Rango de Fechas</span>
          </button>
        </div>
      </div>

      <!-- Botón Depósitos -->
      <Link 
        href="/deposits" 
        class="px-3.5 py-2 bg-white border border-gray-300 hover:border-gray-400 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center gap-2 transition-colors font-medium shadow-sm text-sm"
        title="Administrar depósitos y almacenes"
      >
        <i class="fa fa-warehouse text-orange-500" />
        <span>Depósitos</span>
      </Link>

      <Link 
        href="/deposits/transfers/all" 
        class="px-3.5 py-2 bg-white border border-gray-300 hover:border-gray-400 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center gap-2 transition-colors font-medium shadow-sm text-sm"
        title="Ver o registrar traslados entre depósitos"
      >
        <i class="fa fa-dolly text-orange-500" />
        <span>Traslados</span>
      </Link>

      <!-- Botón Importar Excel -->
      <button 
        type="button"
        class="px-3.5 py-2 bg-white border border-gray-300 hover:border-gray-400 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center gap-2 transition-colors font-medium shadow-sm text-sm cursor-pointer"
        title="Importar inventario desde Excel"
        @click="$emit('open-import-modal')"
      >
        <i class="fa fa-file-upload text-gray-500" />
        <span>Importar Excel</span>
      </button>

      <!-- Botón Primario: Crear Inventario (Naranja Corporativo) -->
      <Link 
        :href="createUrl" 
        class="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg flex items-center gap-2 transition-colors font-medium shadow-sm text-sm"
      >
        <i class="fa fa-plus" />
        <span>Crear Inventario</span>
      </Link>
    </div>
  </div>
</template>

<script>
import { Link } from '@inertiajs/inertia-vue3'

export default {
  name: 'InventoryHeader',

  components: {
    Link,
  },

  props: {
    title: {
      type: String,
      default: 'Inventarios',
    },
    totalCount: {
      type: Number,
      default: null,
    },
    exportUrl: {
      type: String,
      required: true,
    },
    reportLabel: {
      type: String,
      default: 'Exportar / Reportes',
    },
    createUrl: {
      type: String,
      default: '/inventories/create',
    },
  },

  emits: ['open-import-modal', 'set-report-type'],

  data() {
    return {
      isExportOpen: false,
    }
  },

  mounted() {
    document.addEventListener('click', this.handleOutsideClick)
  },

  beforeUnmount() {
    document.removeEventListener('click', this.handleOutsideClick)
  },

  methods: {
    toggleExportDropdown() {
      this.isExportOpen = !this.isExportOpen
    },

    selectReport(type) {
      this.isExportOpen = false
      this.$emit('set-report-type', type)
    },

    handleOutsideClick(event) {
      if (this.isExportOpen && this.$refs.exportDropdownRef && !this.$refs.exportDropdownRef.contains(event.target)) {
        this.isExportOpen = false
      }
    },
  },
}
</script>
